import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { dataLayer } from '../utils/window';

import CarPartImage from './CarPartImage';
import Price from './Price';
import { LinkButton } from './Buttons';
import Link  from './Link';
import breakpoints from '../styles/breakpoints';

const StyledCarPartListItem = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: stretch;
  flex: 0 0 100%;

  ${breakpoints.medium`
    flex: 0 0 50%;
    padding: 0 10px;
  `};

  ${breakpoints.large`
    padding: 0 10px;
    display: block;
  `};
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-bottom: 20px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  width: 100%;

  ${breakpoints.large`
    flex-direction: row;
    align-items: center;
    margin: 0 -${p => p.theme.gutterSmall};
  `};
`;

const Column = styled.div`
  margin: 0 ${p => p.theme.gutterSmall};
`;

const ImageColumn = styled(Column)`
  flex: 1.2;
  width: 100%;
  margin: 0 0 20px;

  ${breakpoints.large`
    margin: 0 ${p => p.theme.gutterSmall};
  `};
`;

const ContentColumn = styled(Column)`
  flex: 2.5;
`;

const PriceColumn = styled(Column)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: 0 20px;

  ${breakpoints.large`
    justify-content: flex-end;
    flex: 0.8;
    margin: 0 ${p => p.theme.gutterSmall};
  `};
`;

const PriceColumnInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${breakpoints.large`
    align-items: flex-start;
    flex-direction: column;
  `};
`;

const Name = styled.h2`
  color: ${p => p.theme.colors.lightFont};
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0.2px;
  margin-bottom: 10px;

  a {
    color: ${p => p.theme.colors.lightFont};
    text-decoration: none;
  }
`;

const StockNumber = styled.div`
  color: ${p => p.theme.colors.lightFont};
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.57;
  letter-spacing: 0.1px;
`;

const Quality = styled.div`
  color: ${p => p.theme.colors.lightFont};
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.57;
  letter-spacing: 0.1px;
`;

const Actions = styled.div`
  margin-top: auto;
`;

const StyledPrice = styled(Price)`
  ${breakpoints.large`
    margin-bottom: 20px;
  `};
`;

const ReadMore = styled(LinkButton)`
  margin-bottom: 20px;
  width: 143px;
  text-align: center;
`;

const SendRequest = styled(Link)`
  color: ${p => p.theme.colors.lightFont};
  font-size: 0.875rem;
  display: block;

  &:hover {
    color: ${p => p.theme.colors.darkFont};
    text-decoration: none;
  }
`;

const InfoText = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.57;
  letter-spacing: 0.1px;
  text-align: left;
  color: #1d1d1d;
`;

export default function CarPartListItem({ carPart }) {
  const { id, name, stockNumber, quality, price, images, country } = carPart;

  const pushAnalyticsDataLayer = () => {
    dataLayer.push({
      'event': 'productClick',
      'ecommerce': {
        'click': {
          'actionField': {'list': 'Bildeler'},
          'products': [{
            ...carPart
           }]
         }
       }
    });
  };

  return (
    <StyledCarPartListItem id={id}>
      <Row>
        <ImageColumn>
          <Link to={`/bildeler/${id}`} onClick={pushAnalyticsDataLayer}>
            <CarPartImage src={images[0]} />
          </Link>
        </ImageColumn>

        <ContentColumn>
          <Name>
            <Link to={`/bildeler/${id}`} onClick={pushAnalyticsDataLayer}>{name}</Link>
          </Name>

          <InfoText>
            {country === 'Norge' && 'Forventet leveringstid 2-3 dager.'}
            {country === 'Sverige' && 'Forventet leveringstid 5-7 dager.'}
          </InfoText>

          <StockNumber>
            <strong>Lagernummer:</strong> {stockNumber}
          </StockNumber>

          {quality && (
            <Quality>
              <strong>Kvalitet:</strong> {quality}
            </Quality>
          )}
        </ContentColumn>

        <PriceColumn>
          <PriceColumnInner>
            <StyledPrice dark amount={price} />

            <Actions>
              <ReadMore to={`/bildeler/${id}`} onClick={pushAnalyticsDataLayer}>Kjøp</ReadMore>

              <SendRequest
                to={`/foresporsel/?id=${id}&lagernummer=${stockNumber}&delenavn=${name}`}
              >
                Send forespørsel »
              </SendRequest>
            </Actions>
          </PriceColumnInner>
        </PriceColumn>
      </Row>
    </StyledCarPartListItem>
  );
}

CarPartListItem.propTypes = {
  carPart: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    supplierId: PropTypes.string,
    supplierName: PropTypes.string,
    stockNumber: PropTypes.string,
    quality: PropTypes.string,
    price: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string)
  }).isRequired
};
